import { FormEvent, useEffect, useMemo, useRef } from "react";
import { CounterInputV2, DropdownV2, FormCardV2, FormGridV2, InputV2, MultiDropdownV2, OptionV2 } from "../FormControlsV2";
import { FormActions } from "./FormActions";
import { BatchCategoryValueV2, BatchFormField, BatchFormProps, BatchFormValueV2 } from "./types";
import { isDisabled } from "./utils";
import { useTranslation } from "react-i18next";
import { translateAnimalCategory, translateAnimalCategoryAge } from "../../i18n/translateAnimalCategory";

function getCategoryInfo(option: OptionV2) {
  return option.info || {};
}

function getCategoryLabel(option: OptionV2) {
  const info = getCategoryInfo(option);
  return translateAnimalCategory(info.name || option.label, info.age_description || info.ageDescription);
}

function getCategoryDescription(option: OptionV2) {
  const info = getCategoryInfo(option);
  const middleWeight = Array.isArray(info.middle_weight) ? info.middle_weight[0] : info.middle_weight;
  const weight = middleWeight || info.middleWeight;
  const age = translateAnimalCategoryAge(info.age_description || info.ageDescription, info.name || option.label);
  if (weight && age) return `${weight}${info.unit ? ` ${info.unit}` : ""} - ${age}`;
  if (weight) return `${weight}${info.unit ? ` ${info.unit}` : ""}`;
  return age || "";
}

function getMiddleWeight(value: any) {
  const middleWeight = Array.isArray(value) ? value[0] : value;
  return Number(middleWeight || 0);
}

function normalizeCategory(option: OptionV2, current?: BatchCategoryValueV2): BatchCategoryValueV2 {
  const info = getCategoryInfo(option);
  return {
    id: info.id || option.value,
    name: info.name || option.label,
    month: info.month ?? current?.month,
    quantity: current?.quantity ?? 0,
    middle_weight: getMiddleWeight(info.middle_weight ?? info.middleWeight ?? current?.middle_weight),
    unit: info.unit || current?.unit,
    age_description: info.age_description || info.ageDescription || current?.age_description,
  };
}

function calculateTotalUa(categories: BatchCategoryValueV2[]) {
  const total = categories.reduce((sum, category) => {
    return sum + (Number(category.quantity || 0) * Number(category.middle_weight || 0)) / 450;
  }, 0);
  return total.toFixed(2);
}

function getCategoryOption(category: BatchCategoryValueV2, options: OptionV2[]) {
  return (
    options.find((option) => String(option.value) === String(category.id)) || {
      label: category.name || String(category.id),
      value: category.id,
      info: category,
    }
  );
}

export function BatchFormV2({
  value,
  mode = "create",
  errors = {},
  disabledFields = [],
  cardTitle,
  cardDescription,
  showCardHeader = true,
  farms,
  areas,
  microAreas,
  categories,
  initialsPrefix,
  autoGenerateInitials = true,
  suggestedInitials = "",
  initialsDescription,
  showTotalUaField = true,
  mapSlot,
  sideSlot,
  summarySlot,
  headerActions,
  showActions = true,
  onChange,
  onBlur,
  onSubmit,
  onCancel,
  onFarmChange,
  onAreaChange,
  onCurrentMicroAreaChange,
}: BatchFormProps) {
  const { t } = useTranslation();
  const previousAutoInitials = useRef("");
  const manuallyEditedInitials = useRef(Boolean(value.initials));
  const disabled = mode === "readonly";
  const selectedCategoryOptions = useMemo(
    () => value.categories.map((category) => getCategoryOption(category, categories)),
    [categories, value.categories]
  );
  const hasSideContent = !!(mapSlot || sideSlot || summarySlot);

  function update(partial: Partial<BatchFormValueV2>) {
    onChange({ ...value, ...partial });
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSubmit && onSubmit(value);
  }

  function handleFarmChange(farm: OptionV2 | null) {
    onFarmChange && onFarmChange(farm);
    update({
      farm, area: null, current_micro_area: null, micro_area_route_ids: [],
      ...(autoGenerateInitials && !manuallyEditedInitials.current ? { initials: "" } : {}),
    });
  }

  function handleAreaChange(area: OptionV2 | null) {
    onAreaChange && onAreaChange(area);
    update({
      area, current_micro_area: null, micro_area_route_ids: [],
      ...(autoGenerateInitials && !manuallyEditedInitials.current ? { initials: "" } : {}),
    });
  }

  function handleCategoriesChange(selected: OptionV2[]) {
    const nextCategories = selected.map((option) => {
      const current = value.categories.find((category) => String(category.id) === String(option.value));
      return normalizeCategory(option, current);
    });
    update({ categories: nextCategories, total_ua: calculateTotalUa(nextCategories) });
  }

  function handleCategoryQuantityChange(categoryId: string | number, quantity: string | number) {
    const nextCategories = value.categories.map((category) =>
      String(category.id) === String(categoryId) ? { ...category, quantity } : category
    );
    update({ categories: nextCategories, total_ua: calculateTotalUa(nextCategories) });
  }

  function handleCurrentMicroAreaChange(current_micro_area: OptionV2 | null) {
    onCurrentMicroAreaChange && onCurrentMicroAreaChange(current_micro_area);
    update({
      current_micro_area,
      micro_area_route_ids: current_micro_area ? [current_micro_area.value] : [],
    });
  }

  useEffect(() => {
    if (!autoGenerateInitials || mode !== "create" || manuallyEditedInitials.current) return;

    const nextInitials = value.area ? suggestedInitials : "";
    const currentMatchesAutoInitials = !value.initials || value.initials === previousAutoInitials.current;
    previousAutoInitials.current = nextInitials;

    if (currentMatchesAutoInitials && value.initials !== nextInitials) {
      onChange({ ...value, initials: nextInitials });
    }
  }, [autoGenerateInitials, suggestedInitials, mode, onChange, value]);

  const formFields = (
    <div className="ov2-batch-form__fields">
      <FormGridV2>
        <DropdownV2
          name="farm"
          label={t("formsV2.farm")}
          value={value.farm}
          options={farms}
          searchable
          disabled={isDisabled("farm", mode, disabledFields)}
          error={errors.farm}
          onChange={handleFarmChange}
        />
        <DropdownV2
          name="area"
          label={t("formsV2.area")}
          value={value.area}
          options={areas}
          searchable
          disabled={isDisabled("area", mode, disabledFields) || !value.farm}
          error={errors.area}
          onChange={handleAreaChange}
        />
        <DropdownV2
          name="current_micro_area"
          label={t("formsV2.currentPaddock")}
          value={value.current_micro_area}
          options={microAreas}
          searchable
          disabled={isDisabled("current_micro_area", mode, disabledFields) || !value.area}
          error={errors.current_micro_area}
          emptyMessage={t("formsV2.noPaddocks")}
          onChange={handleCurrentMicroAreaChange}
        />
        <InputV2
          name="initials"
          label={t("formsV2.initials")}
          prefix={initialsPrefix}
          description={initialsDescription}
          value={value.initials}
          maxLength={5}
          disabled={isDisabled("initials", mode, disabledFields)}
          error={errors.initials}
          onChange={(initials) => {
            manuallyEditedInitials.current = true;
            update({ initials: initials.toUpperCase() });
          }}
        />

        <MultiDropdownV2
          name="categories"
          label={t("formsV2.category")}
          value={selectedCategoryOptions}
          options={categories}
          getOptionLabel={getCategoryLabel}
          searchable
          disabled={disabled || isDisabled("categories", mode, disabledFields)}
          error={errors.categories}
          emptyMessage={t("formsV2.weightParametersRequired")}
          onChange={handleCategoriesChange}
          renderOption={(option) => {
            const description = getCategoryDescription(option);
            return (
              <span className="ov2-multidropdown__option-text">
                <strong>{getCategoryLabel(option)}</strong>
                {description && <small>{description}</small>}
              </span>
            );
          }}
        />
        {showTotalUaField && (
          <div className="ov2-batch-ua-tag">
            <span>{t("formsV2.totalUa")}</span>
            <strong>{value.total_ua || "0.00"} UA</strong>
            {errors.total_ua && <small className="ov2-field__error animation__fadeIn">{errors.total_ua}</small>}
          </div>
        )}
      </FormGridV2>

      {value.categories.length > 0 && (
        <div className="ov2-batch-categories">
          {value.categories.map((category) => {
            const age = translateAnimalCategoryAge(category.age_description, category.name);
            const errorKey = `categories.${category.id}.quantity` as BatchFormField;
            return (
              <div className="ov2-batch-category" key={`${category.id}`}>
                <div className="ov2-batch-category__info">
                  <strong>{translateAnimalCategory(category.name, category.age_description) || category.id}</strong>
                  {(category.middle_weight || category.unit || age) && (
                    <span className="ov2-batch-category__description">
                      {category.middle_weight}
                      {category.unit ? ` ${category.unit}` : ""}
                      {age ? ` - ${age}` : ""}
                    </span>
                  )}
                </div>
                <CounterInputV2
                  name={`categories.${category.id}.quantity`}
                  value={category.quantity}
                  disabled={disabled}
                  error={errors[errorKey]}
                  onChange={(quantity) => handleCategoryQuantityChange(category.id, quantity)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <form className="ov2-property-form ov2-batch-form" onBlur={onBlur} onSubmit={handleSubmit}>
      <FormCardV2
        title={showCardHeader ? cardTitle || t("formsV2.batch") : undefined}
        description={showCardHeader ? cardDescription : undefined}
        actions={headerActions}
      >
        {hasSideContent ? (
          <div className="ov2-property-form__layout">
            {formFields}
            <div className="ov2-property-form__map">
              {mapSlot}
              {summarySlot}
              {sideSlot}
            </div>
          </div>
        ) : (
          formFields
        )}
      </FormCardV2>

      {showActions && <FormActions mode={mode} onCancel={onCancel} />}
    </form>
  );
}
